import { useEffect, useMemo } from 'react';
import { useTranslation } from '@client-app/i18n';
import {
    MCP_OAUTH_RESULT_CHANNEL,
    MCP_OAUTH_RESULT_MESSAGE,
} from '@/components/Settings/components/integrations/useMcpConnectorFlow';
import { parseMcpOAuthReturnParams } from '@/components/Settings/components/integrations/mcpOAuthReturnParams';

type CallbackView = 'success' | 'error' | 'malformed';

const McpOAuthCallbackPage = () => {
    const { t } = useTranslation();
    const oauthReturn = useMemo(
        () => parseMcpOAuthReturnParams(window.location.search),
        [],
    );
    const view: CallbackView = oauthReturn?.result || 'malformed';

    useEffect(() => {
        if (!oauthReturn) {
            return undefined;
        }

        const message = {
            type: MCP_OAUTH_RESULT_MESSAGE,
            ...oauthReturn,
        };

        try {
            const channel = new BroadcastChannel(MCP_OAUTH_RESULT_CHANNEL);
            channel.postMessage(message);
            channel.close();
        } catch {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            window.close();
        }, 500);
        return () => window.clearTimeout(timeoutId);
    }, [oauthReturn]);

    const titleKey = view === 'success'
        ? 'website.integrationsSettings.mcpOAuthReturnSuccessTitle'
        : view === 'error'
            ? 'website.integrationsSettings.mcpOAuthReturnErrorTitle'
            : 'website.integrationsSettings.mcpOAuthReturnMalformedTitle';
    const descriptionKey = view === 'success'
        ? 'website.integrationsSettings.mcpOAuthReturnSuccessDescription'
        : view === 'error'
            ? 'website.integrationsSettings.mcpOAuthReturnErrorDescription'
            : 'website.integrationsSettings.mcpOAuthReturnMalformedDescription';

    return (
        <div className="flex min-h-screen items-center justify-center bg-white px-6 text-center">
            <div className="max-w-md">
                <h1 className="text-xl font-semibold text-[#1A162F]">{t(titleKey)}</h1>
                <p className="mt-2 text-sm leading-6 text-[#666F8D]">{t(descriptionKey)}</p>
                <button
                    type="button"
                    onClick={() => window.close()}
                    className="mt-5 rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-medium text-white"
                >
                    {t('website.integrationsSettings.mcpOAuthReturnClose')}
                </button>
            </div>
        </div>
    );
};

export default McpOAuthCallbackPage;
